import React from 'react';
import { Heart, User, Target, Compass, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ContentAccordion from '../components/common/ContentAccordion';
import { retreatContent } from '../data/retreatContent';

const pillarData = [
  {
    key: 'Relationship',
    icon: Heart,
    color: 'from-rose-500 to-rose-600',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-200',
    weeks: '1',
    days: '1-7',
    description: 'God knows me deeply and personally. This week focuses on recognizing God\'s intimate knowledge of you and His unconditional love.',
    questions: [
      'How do I experience God\'s presence in my daily life?',
      'What barriers prevent me from deeper relationship with God?',
      'How can I make more time for prayer and reflection?'
    ]
  },
  {
    key: 'Identity',
    icon: User,
    color: 'from-amber-500 to-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    weeks: '2',
    days: '8-14',
    description: 'I am known and loved by God. This week explores your identity as a beloved child of God and the gifts He has given you.',
    questions: [
      'Who am I in the eyes of God?',
      'What gifts and talents has God given me?',
      'How do I see myself compared to how God sees me?'
    ]
  },
  {
    key: 'Mission',
    icon: Target,
    color: 'from-teal-500 to-teal-600',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200',
    weeks: '3',
    days: '15-21',
    description: 'God sends me to serve others. This week focuses on discovering how God is calling you to use your gifts in service.',
    questions: [
      'Where is God calling me to serve?',
      'How can I use my gifts to help others?',
      'What holds me back from saying "yes" to God\'s call?'
    ]
  },
  {
    key: 'Purpose',
    icon: Compass,
    color: 'from-violet-500 to-violet-600',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
    weeks: '4',
    days: '22-30',
    description: 'I flourish when I live my calling. This week brings together relationship, identity, and mission into a unified sense of purpose.',
    questions: [
      'What gives my life meaning and purpose?',
      'How does my faith inform my daily decisions?',
      'What legacy do I want to leave?'
    ]
  }
];

const RIMPPage = () => {
  const navigate = useNavigate();

  return (
    <div className="px-4 py-6 max-w-lg mx-auto space-y-6">
      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="font-heading text-2xl text-[var(--primary)] font-bold mb-2">
          The RIMP Way
        </h1>
        <p className="text-gray-500 text-sm">
          Relationship • Identity • Mission • Purpose
        </p>
      </div>

      {/* Intro */}
      <div className="bg-gradient-to-br from-[var(--primary)] to-[var(--primary-dark)] rounded-xl p-5 text-white">
        <p className="leading-relaxed text-sm">
          The RIMP Way is a framework for spiritual growth inspired by the Spiritual Exercises of St. Ignatius Loyola. 
          It guides you through four interconnected dimensions of the spiritual life, helping you grow in your 
          relationship with God and flourish in your daily mission.
        </p>
      </div>

      {/* Four Pillars */}
      <div className="space-y-4">
        {pillarData.map((pillar) => {
          const Icon = pillar.icon;
          return (
            <ContentAccordion
              key={pillar.key}
              title={
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${pillar.color} flex items-center justify-center`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <span className="font-semibold">{pillar.key}</span>
                    <span className="text-xs text-gray-400 ml-2">Week {pillar.weeks} • Days {pillar.days}</span>
                  </div>
                </div>
              }
              summary={pillar.description.substring(0, 60) + '...'}
            >
              <div className="space-y-4">
                <p className="text-gray-700 text-sm leading-relaxed">
                  {pillar.description}
                </p>
                
                <div className={`${pillar.bgColor} rounded-lg p-3 ${pillar.borderColor} border`}>
                  <h5 className="font-medium text-gray-800 text-sm mb-2">Reflection Questions:</h5>
                  <ul className="space-y-1">
                    {pillar.questions.map((q, idx) => (
                      <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                        <span className="text-gray-400">•</span>
                        {q}
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => navigate(`/day/${pillar.days.split('-')[0]}`)}
                  className="w-full flex items-center justify-center gap-2 bg-[var(--primary)] text-white py-2 rounded-lg text-sm font-medium hover:bg-[var(--primary-dark)] transition-colors"
                >
                  Start Week {pillar.weeks}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </ContentAccordion>
          );
        })}
      </div>

      {/* Quote */}
      <div className="bg-amber-50 rounded-xl p-4 border border-amber-100 text-center">
        <p className="text-gray-700 italic text-sm leading-relaxed">
          "The glory of God is a human being fully alive."
        </p>
        <p className="text-gray-500 text-xs mt-2">— St. Irenaeus</p>
      </div>

      {/* Integration Note */}
      <ContentAccordion title="How the Four Pillars Connect" summary="Understanding the RIMP journey">
        <div className="space-y-3 text-sm text-gray-600">
          <p>
            The four pillars of RIMP are not separate stages but interconnected dimensions of spiritual growth:
          </p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <Heart className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" />
              <span><strong>Relationship</strong> with God is the foundation of everything.</span>
            </li>
            <li className="flex items-start gap-2">
              <User className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <span><strong>Identity</strong> flows from knowing you are loved by God.</span>
            </li>
            <li className="flex items-start gap-2">
              <Target className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
              <span><strong>Mission</strong> emerges when you share your God-given gifts.</span>
            </li>
            <li className="flex items-start gap-2">
              <Compass className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" />
              <span><strong>Purpose</strong> is the integration of all three in daily life.</span>
            </li>
          </ul>
          <p>
            As you journey through the 30 days, you'll find these themes weaving together, 
            building upon each other to create a holistic spiritual experience.
          </p>
        </div>
      </ContentAccordion>
    </div>
  );
};

export default RIMPPage;
